import { SearchParamError } from '@tanstack/react-router';

/** Whether a route's query param has to be in the URL. */
export type SearchParamPresence = 'required' | 'optional';

/** A route's query params, each flat string declared `'required'` or `'optional'`. */
export type StringSearchSpec = Record<string, SearchParamPresence>;

type KeysOf<T extends StringSearchSpec, TPresence extends SearchParamPresence> = {
    [K in keyof T]: T[K] extends TPresence ? K : never;
}[keyof T];

/** The search a route reads and a link must pass; an empty spec allows no key, where `{}` would accept any. */
// Not an index signature, which would type other routes' keys `undefined` where the router merges searches:
// a search updater's `prev`, `useSearch({ strict: false })`, and a layout's search intersected with its children's.
export type StringSearch<T extends StringSearchSpec> = keyof T extends never
    ? {
          /** Never set: the one key of a weak type, so an object with any other key fails to compile. */
          readonly __noSearchParams?: never;
      }
    : { [K in KeysOf<T, 'required'>]: string } & { [K in KeysOf<T, 'optional'>]?: string };

/** What `stringSearch` returns for a URL without one of its required params: one issue per missing key. */
export interface StringSearchFailure {
    readonly issues: ReadonlyArray<{ readonly message: string; readonly path: ReadonlyArray<PropertyKey> }>;
}

/** A Standard Schema v1 validator (https://standardschema.dev), which the router reads its types from, so this API names no router type. */
export interface StringSearchValidator<T extends StringSearchSpec> {
    readonly '~standard': {
        readonly version: 1;
        readonly vendor: 'luminovo';
        readonly validate: (
            value: unknown,
        ) => { readonly value: StringSearch<T>; readonly issues?: undefined } | StringSearchFailure;
        /** Type-only, as the spec has it: never set at runtime. */
        readonly types?: { readonly input: StringSearch<T>; readonly output: StringSearch<T> };
    };
}

/** A route's `validateSearch` for flat strings; a missing required param renders `NotFoundPage`; URLs stay as is. */
// A route with boolean, number, list or enum params declares them all in a `z.object` with `searchParam` instead.
export function stringSearch<const T extends StringSearchSpec>(spec: T): StringSearchValidator<T> {
    const keys = Object.keys(spec);

    return {
        '~standard': {
            version: 1,
            vendor: 'luminovo',
            validate: (value) => {
                const raw = (typeof value === 'object' && value !== null ? value : {}) as Record<string, unknown>;
                const search: Record<string, string> = {};
                const missing: string[] = [];
                for (const key of keys) {
                    const param = raw[key];
                    // An empty value counts as present; undeclared params stay in the raw search the router keeps.
                    if (typeof param === 'string') {
                        search[key] = param;
                    } else if (spec[key] === 'required') {
                        missing.push(key);
                    }
                }
                // The router then renders the route's error component before its `beforeLoad` runs.
                if (missing.length > 0) {
                    return {
                        issues: missing.map((key) => ({
                            message: `Missing required query param "${key}"`,
                            path: [key],
                        })),
                    };
                }
                return { value: search as StringSearch<T> };
            },
        },
    };
}

/**
 * Whether a route failed because a param its `validateSearch` requires is missing (or, for `searchParam`,
 * unreadable). The app's route error component renders `NotFoundPage` for it, and doesn't report it.
 *
 * `SearchParamError`'s `cause` is the failed validator's result, with its `issues`, or an exception the validator
 * threw: a bug, so not this error. The routes' validators fail only on required params (`searchParam` reads an
 * unreadable optional one as `undefined`).
 */
export function isMissingSearchParamError(error: unknown): boolean {
    return error instanceof SearchParamError && hasIssues(error.cause);
}

function hasIssues(cause: unknown): boolean {
    return typeof cause === 'object' && cause !== null && Array.isArray((cause as { issues?: unknown }).issues);
}
